// Pure derivations for the FrameX pipeline.
//
// Deliberately dependency-free: no stores, no Svelte, no `#src` imports, so
// these can be unit-tested under `node --test` on Node 18.

/** A sheet has a usable drawing scale. */
export function isScaled(page) {
  return Boolean(page?.scale);
}

/** The sheet was skipped on purpose — distinct from "no scale yet". */
export function isNotApplicable(page) {
  return Boolean(page?.notApplicable);
}

/**
 * Membership in the FrameX working set. Derived rather than stored: a sheet
 * joins when a scale is applied, never by being selected, and marking it N/A
 * keeps it out.
 */
export function isInFrameX(page) {
  return isScaled(page) && !isNotApplicable(page) && !page?.framexExcluded;
}

/** Frames on a sheet: collection locations that actually have a boundary. */
export function sheetFrames(locations) {
  return (locations || []).filter(
    (l) => l.record_type === "collection" && l.clip,
  );
}

/** Built = has both a width and a height, however entered (analyzer or hand). */
export function isFrameBuilt(item) {
  return Boolean(item?.width && item?.height);
}

/** The working set, in document order. */
export function workingSheetIds(pages, order) {
  return (order || []).filter((id) => isInFrameX(pages?.[id]));
}

/** "Locator ran" and "has frames" are separate facts; either satisfies the step. */
export function hasBeenLocated(page, locations) {
  return Boolean(page?.detectDone) || sheetFrames(locations).length > 0;
}

/**
 * Frame placements on sheets NOT in the FrameX working set — the "Not in
 * FrameX" list. Placement-based, mirroring `sheetFrames`/`allFrames`: one row
 * per clipped collection placement on a non-working sheet, in document order.
 * A frame placed on both an in- and an out-of-FrameX sheet appears above for
 * its in-sheet placement and here for its out-sheet one.
 */
export function framesNotInFrameX(indexedLocations, order, workingIds) {
  const working = new Set(workingIds || []);
  return (order || [])
    .filter((id) => !working.has(id))
    .flatMap((id) => sheetFrames(indexedLocations?.[id]));
}

/** The frame's item carries the clipped image for this placement. */
export function isFrameImaged(loc, grp) {
  const rec = grp?.items?.[loc?.record_id];
  return Boolean(loc?.id && rec?.drawing?.attachment_id === loc.id);
}

/** The record's drawing is attached to one of the record's own placements —
    some placement owns the image, even if it isn't this one. An item has a
    single attachment_id, so with several placements only one can satisfy
    isFrameImaged; "repairing" another placement just steals the attachment
    and un-images its sibling, and the tracker ping-pongs forever (the
    paste/Ctrl+V OOM freeze). Repair decisions must use this record-scoped
    test, keeping isFrameImaged for per-placement ownership. */
export function isRecordImaged(loc, grp) {
  const rec = grp?.items?.[loc?.record_id];
  const attached = grp?.locations?.[rec?.drawing?.attachment_id];
  return Boolean(attached && attached.record_id === loc?.record_id);
}
